import type { ComponentConfig } from '@measured/puck';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, CheckCircle2, Sparkles, TrendingUp } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { CountUp } from '../motion/CountUp';
import { easeStripe } from '../../lib/motion';

export interface HeroMinimalProps {
  eyebrow?: string;
  title?: string;
  titleAccent?: string;
  subtitle?: string;
  /** Alias of `subtitle` (used by audience-aware home content). */
  sub?: string;
  /** Optional chip text displayed under CTAs (audience-aware). */
  chip?: string;
  ctaPrimaryLabel?: string;
  ctaPrimaryHref?: string;
  ctaSecondaryLabel?: string;
  ctaSecondaryHref?: string;
  /** Audience-aware CTA object (overrides ctaPrimaryLabel/Href if provided). */
  primaryCta?: { label: string; href: string; iconName?: string };
  /** Audience-aware CTA object (overrides ctaSecondaryLabel/Href if provided). */
  secondaryCta?: { label: string; href: string };
}

interface PoleTag {
  label: string;
  code: string;
  accent?: boolean;
}

const POLE_TAGS: readonly PoleTag[] = [
  { label: 'Travaux & rénovation', code: 'TRV' },
  { label: 'Courtage énergie', code: 'CRT' },
  { label: 'Mandataire CEE', code: 'CEE', accent: true },
];

const TRUST_ITEMS = [
  { label: 'Décret 25 mars 2022', value: 'Mandataire CEE déclaré' },
  { label: '100 % gratuit', value: 'Diagnostic offert' },
  { label: 'Estimation immédiate', value: '60 sec' },
] as const;

export function HeroMinimal({
  eyebrow = 'Primes énergie, Rénovation, Comparaison de contrats',
  title = 'Encaissez vos primes.',
  titleAccent = 'Allégez vos factures.',
  subtitle,
  sub,
  chip,
  ctaPrimaryLabel,
  ctaPrimaryHref,
  ctaSecondaryLabel,
  ctaSecondaryHref,
  primaryCta,
  secondaryCta,
}: HeroMinimalProps) {
  const resolvedSubtitle =
    sub ??
    subtitle ??
    'Mandataire CEE, courtier en énergie et opérateur travaux. On chiffre vos aides, on négocie vos contrats et on dépose vos dossiers — pour particuliers, pros, collectivités et agriculteurs. Un seul interlocuteur, zéro avance.';
  const primaryLabel = primaryCta?.label ?? ctaPrimaryLabel ?? 'Estimer mes aides en 60 sec';
  const primaryHref = primaryCta?.href ?? ctaPrimaryHref ?? '/outils';
  const secondaryLabel = secondaryCta?.label ?? ctaSecondaryLabel ?? 'Voir tous nos outils';
  const secondaryHref = secondaryCta?.href ?? ctaSecondaryHref ?? '/outils';

  return (
    <section className="relative overflow-hidden bg-paper">
      {/* Background Vercel/Linear : 1 spot glow + dot grid masqué */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="dot-grid-masked absolute inset-0" />
        <div className="spot-glow" />
      </div>

      {/* Hairline border bas */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent"
      />

      <Container className="relative pb-20 pt-14 md:pb-28 md:pt-20">
        <div className="grid gap-12 md:grid-cols-2 md:items-center md:gap-10 lg:grid-cols-[1.05fr,1fr] lg:gap-16">
          {/* ─────────── Colonne gauche : copy ─────────── */}
          <div>
            {/* Eyebrow chip Linear-style */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: easeStripe }}
              className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur"
            >
              <span className="live-dot" aria-hidden />
              <span>{eyebrow}</span>
            </motion.div>

            {/* Titre — accent unique mono Linear-style */}
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.06, ease: easeStripe }}
              className="mt-6 text-balance font-display text-[clamp(2.25rem,5vw,3.5rem)] font-bold leading-[1.02] tracking-[-0.03em] text-ink"
            >
              {title}
              {titleAccent && (
                <>
                  {' '}
                  <span className="text-gradient-mono">{titleAccent}</span>
                </>
              )}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.14, ease: easeStripe }}
              className="mt-5 max-w-xl text-[15px] leading-[1.6] text-stone-600"
            >
              {resolvedSubtitle}
            </motion.p>

            {/* Tags mono Linear-style (au lieu de pills colorées) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.22, ease: easeStripe }}
              className="mt-7 flex flex-wrap items-center gap-1.5"
            >
              {POLE_TAGS.map((tag) => (
                <span
                  key={tag.label}
                  className={tag.accent ? 'tag-mono tag-mono-accent' : 'tag-mono'}
                >
                  <span className="text-[10px] tabular-nums opacity-50">{tag.code}</span>
                  <span>{tag.label}</span>
                </span>
              ))}
            </motion.div>

            {/* CTAs Vercel-style : ink solid + ghost secondary */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3, ease: easeStripe }}
              className="mt-8 flex flex-wrap items-center gap-2.5"
            >
              <Button asChild size="lg" variant="primary" className="group">
                <a href={primaryHref} aria-label={primaryLabel}>
                  {primaryLabel}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                </a>
              </Button>
              <Button asChild size="lg" variant="secondary" className="group">
                <a href={secondaryHref} aria-label={secondaryLabel}>
                  {secondaryLabel}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
                </a>
              </Button>
            </motion.div>

            {/* Chip optionnel (audience-aware) */}
            {chip && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.36, ease: easeStripe }}
                className="mt-4 inline-flex items-center gap-1.5 text-[11.5px] text-stone-500"
              >
                <span className="inline-block h-1 w-1 rounded-full bg-stone-400" />
                {chip}
              </motion.p>
            )}

            {/* Trust bar Vercel-style — séparateurs verticaux */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.42, ease: easeStripe }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3"
            >
              {TRUST_ITEMS.map((item, i) => (
                <div key={item.label} className="flex items-center gap-3">
                  {i > 0 && (
                    <span className="hidden h-3 w-px bg-stone-200 md:inline-block" aria-hidden />
                  )}
                  <div className="flex flex-col">
                    <span className="font-mono text-[11px] font-semibold tabular-nums text-ink">
                      {item.value}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.12em] text-stone-500">
                      {item.label}
                    </span>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* ─────────── Colonne droite : Live card (Linear/Vercel style) ─────────── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18, ease: easeStripe }}
            className="relative overflow-clip"
          >
            {/* Halo subtil derrière la card */}
            <div
              aria-hidden
              className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-brand-100/30 via-paper to-accent-100/20 blur-3xl"
            />

            {/* Live preview card — produit miniature */}
            <div className="live-card p-6 md:p-7">
              {/* Header card */}
              <div className="flex items-start justify-between gap-3 border-b border-stone-100 pb-5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="live-dot" aria-hidden />
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                      Données consolidées, T1 2026
                    </span>
                  </div>
                  <p className="mt-1.5 font-display text-[15px] font-semibold leading-tight text-ink">
                    Primes versées à nos clients
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-md border border-brand-200/60 bg-brand-50/80 px-2 py-1 font-mono text-[10px] font-semibold text-brand-700">
                  <TrendingUp className="h-2.5 w-2.5" strokeWidth={2.4} />
                  <span className="tabular-nums">+24%</span>
                </span>
              </div>

              {/* Big metric */}
              <div className="mt-5">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-[44px] font-bold tabular-nums leading-none tracking-[-0.03em] text-ink md:text-5xl">
                    <CountUp end={7.8} decimals={1} />
                  </span>
                  <span className="font-display text-2xl font-semibold text-stone-500">M€</span>
                </div>
                <p className="mt-2 font-mono text-[11px] text-stone-500">
                  vs <span className="tabular-nums">6.3 M€</span> en 2025
                </p>
              </div>

              {/* Segment bars — Linear-style */}
              <div className="mt-6 space-y-3">
                {[
                  { label: 'Particuliers', pct: 32, value: '2.5 M€', color: 'bg-brand-500' },
                  { label: 'Entreprises', pct: 38, value: '3.0 M€', color: 'bg-ink' },
                  { label: 'Artisans RGE', pct: 30, value: '2.3 M€', color: 'bg-accent-500' },
                ].map((seg, i) => (
                  <div key={seg.label}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 rounded-sm ${seg.color}`} aria-hidden />
                        <span className="text-[12px] font-medium text-stone-700">{seg.label}</span>
                      </div>
                      <div className="flex items-center gap-2 font-mono text-[11px] tabular-nums text-stone-500">
                        <span>{seg.value}</span>
                        <span className="font-semibold text-ink">{seg.pct}%</span>
                      </div>
                    </div>
                    <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-stone-100">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${seg.pct}%` }}
                        transition={{ duration: 0.9, delay: 0.5 + i * 0.08, ease: easeStripe }}
                        className={`h-full rounded-full ${seg.color}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer card — réassurances mono */}
              <div className="mt-6 flex items-center justify-between border-t border-stone-100 pt-4">
                <div className="flex items-center gap-1.5 text-[11px] text-stone-500">
                  <CheckCircle2 className="h-3 w-3 text-brand-600" strokeWidth={2} />
                  <span>Données vérifiées, registre officiel CEE</span>
                </div>
                <a
                  href="/a-propos"
                  className="group/lnk inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-ink transition-colors hover:text-brand-700"
                >
                  Notre méthode
                  <ArrowUpRight className="h-3 w-3 transition-transform duration-150 ease-out group-hover/lnk:-translate-y-0.5 group-hover/lnk:translate-x-0.5" />
                </a>
              </div>
            </div>

            {/* Floating badge — service phare */}
            <motion.div
              initial={{ opacity: 0, x: 16, y: -8 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: easeStripe }}
              className="absolute -right-2 -top-3 hidden md:block"
            >
              <div className="inline-flex items-center gap-1.5 rounded-full border border-accent-200/70 bg-white px-2.5 py-1 shadow-card">
                <Sparkles className="h-3 w-3 text-accent-700" strokeWidth={2} />
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-accent-800">
                  Service phare
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}

export const HeroMinimalConfig: ComponentConfig<HeroMinimalProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'textarea' },
    titleAccent: { type: 'text' },
    subtitle: { type: 'textarea' },
    ctaPrimaryLabel: { type: 'text' },
    ctaPrimaryHref: { type: 'text' },
    ctaSecondaryLabel: { type: 'text' },
    ctaSecondaryHref: { type: 'text' },
  },
  defaultProps: {
    eyebrow: 'Primes énergie, Rénovation, Comparaison de contrats',
    title: 'Encaissez vos primes.',
    titleAccent: 'Allégez vos factures.',
    subtitle:
      'Mandataire CEE, courtier en énergie et opérateur travaux. On chiffre vos aides, on négocie vos contrats et on dépose vos dossiers — pour particuliers, pros, collectivités et agriculteurs. Un seul interlocuteur, zéro avance.',
    ctaPrimaryLabel: 'Estimer mes aides en 60 sec',
    ctaPrimaryHref: '/outils',
    ctaSecondaryLabel: 'Voir tous nos outils',
    ctaSecondaryHref: '/outils',
  },
  render: HeroMinimal,
};
